import React from "react";
import {
  Droplets,
  Moon,
  TreePine,
  Flame,
  Bird,
  Users,
  Plus,
  Star,
  Calendar,
  Mountain,
  MessageCircle,
} from "lucide-react";
import PageHero from "../components/blocks/PageHero";
import Section from "../components/ui/Section";
import PremiumButton from "../components/ui/PremiumButton";
import mockData from "../mockData";
import useSEO from "../hooks/useSEO";
import { whatsappUrl } from "../lib/email";

const iconMap = {
  Droplets,
  Moon,
  TreePine,
  Flame,
  Bird,
  Users,
};

// Experience images
const starGazingImageId = "1z2PQmfavpU6hW6Wm0EQLKwkHVkHW5DVt";
const getProxyUrl = (id) => {
  return `${process.env.REACT_APP_BACKEND_URL}/api/media/proxy?url=${encodeURIComponent(`https://drive.google.com/uc?export=view&id=${id}`)}`;
};

// Off-Roading Gallery Images
const offRoadImages = {
  main: "/glowlawns-poilochal-chappanthottam-offroad-view.webp",
  cottage: "/glowlawns-offroad-jeep-cottage-wayanad.webp",
  jeep: "/glowlawns-4x4-offroad-jeep-wayanad.webp",
  view: "/glowlawns-poilochal-chappanthottam-offroad-view.webp",
};

const Card = ({ children }) => (
  <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-md">
    {children}
  </div>
);

const Experiences = () => {
  useSEO('/experiences');
  const included = mockData.experiences.filter((x) => x.included);
  const addons = mockData.experiences.filter((x) => x.addon);

  return (
    <div className="min-h-screen bg-[#050706] text-[#F4F1E8] pt-16">
      <PageHero
        title="Experiences"
        subtitle="Moments that transform"
        image="https://images.unsplash.com/photo-1432405972618-c60b0225b8f9?auto=format&fit=crop&w=2400&q=80"
      />

      {/* Star Gazing Feature Section */}
      <Section className="py-16 border-b border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Image */}
          <div className="relative overflow-hidden rounded-3xl">
            <img
              src={getProxyUrl(starGazingImageId)}
              alt="Star Gazing at Vattippana View Point"
              className="w-full h-[400px] md:h-[500px] object-cover"
              loading="lazy"
              data-testid="stargazing-image"
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            
            {/* Badge */}
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C9A227]/20 border border-[#C9A227]/40 backdrop-blur-sm">
              <Star className="w-4 h-4 text-[#C9A227]" fill="currentColor" />
              <span className="text-xs uppercase tracking-wider text-[#C9A227] font-medium">Featured Experience</span>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-6">
            <div>
              <div className="inline-flex items-center gap-2 text-[#C9A227] mb-3">
                <Star className="w-5 h-5" />
                <span className="text-sm uppercase tracking-widest">Celestial Experience</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white leading-tight" data-testid="stargazing-title">
                Star Gazing at<br />
                <span className="text-[#C9A227]">Vattippana View Point</span>
              </h2>
            </div>

            <p className="text-base md:text-lg text-white/70 leading-relaxed">
              Experience the magic of the cosmos from Vattippana View Point — one of Kavilumpara's most 
              breathtaking vantage points. With minimal light pollution and clear mountain skies, 
              watch as thousands of stars reveal themselves against the infinite darkness.
            </p>

            <div className="space-y-3 text-white/60 text-sm">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                <span>Unobstructed 360° panoramic views of the night sky</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                <span>Best visibility during new moon nights</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                <span>Guided stargazing sessions available on request</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                <span>Capture the Milky Way in all its glory</span>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Users className="w-5 h-5 text-white/50" />
              <span className="text-white/60">Perfect for couples, families & photography enthusiasts</span>
            </div>

            <div className="pt-4">
              <PremiumButton href="/contact" variant="primary" className="px-8 py-4" data-testid="stargazing-book-btn">
                <span className="inline-flex items-center gap-2">
                  <Calendar size={18} />
                  Book This Experience
                </span>
              </PremiumButton>
            </div>
          </div>
        </div>
      </Section>

      {/* ===== OFF-ROADING EXPERIENCE SECTION ===== */}
      
      {/* 1. Description Block - Two Column Layout */}
      <Section className="py-16 border-b border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Image - Left */}
          <div className="relative overflow-hidden rounded-2xl group">
            <img
              src={offRoadImages.main}
              alt="Mountain off-roading experience at Poilochal Chappanthottam near Glow Lawns Resort Kerala"
              className="w-full h-[400px] md:h-[500px] object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
              data-testid="offroad-main-image"
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            
            {/* Badge */}
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C9A227]/20 border border-[#C9A227]/40 backdrop-blur-sm">
              <Mountain className="w-4 h-4 text-[#C9A227]" />
              <span className="text-xs uppercase tracking-wider text-[#C9A227] font-medium">Adventure Experience</span>
            </div>
          </div>

          {/* Content - Right */}
          <div className="space-y-6">
            <div>
              <div className="inline-flex items-center gap-2 text-[#C9A227] mb-3">
                <Mountain className="w-5 h-5" />
                <span className="text-sm uppercase tracking-widest">4x4 Trail Experience</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white leading-tight" data-testid="offroading-title">
                An Authentic Kerala<br />
                <span className="text-[#C9A227]">Off-Road Trail</span>
              </h2>
            </div>

            <div className="space-y-4 text-base md:text-lg text-white/70 leading-relaxed">
              <p>
                At GlowLAWNS Moonrise Valley, adventure is more than a stay — it's an experience.
              </p>
              <p>
                Our exclusive 4x4 uphill trail at Poilochal Chappanthottam takes you through rugged paths, 
                forest terrain, and panoramic mountain landscapes.
              </p>
              <p>
                Feel the power of true off-road climbing as you ascend through winding slopes surrounded 
                by untouched natural beauty.
              </p>
            </div>

            <div className="pt-2">
              <div className="text-sm text-white font-medium mb-4">Perfect for:</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-3 text-white/60 text-sm">
                  <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                  <span>Adventure seekers</span>
                </div>
                <div className="flex items-center gap-3 text-white/60 text-sm">
                  <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                  <span>Jeep enthusiasts</span>
                </div>
                <div className="flex items-center gap-3 text-white/60 text-sm">
                  <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                  <span>Groups & friends</span>
                </div>
                <div className="flex items-center gap-3 text-white/60 text-sm">
                  <div className="w-2 h-2 rounded-full bg-[#C9A227]" />
                  <span>Nature explorers</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* 2. Gallery Strip - 3 Image Grid */}
      <Section className="py-16 border-b border-white/10">
        <div className="text-center mb-10">
          <h3 className="text-2xl md:text-3xl font-light text-white">
            Capture the <span className="text-[#C9A227]">Adventure</span>
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {/* Image 1 */}
          <div className="relative overflow-hidden rounded-lg group shadow-[0_8px_30px_rgba(0,0,0,0.3)]">
            <img
              src={offRoadImages.cottage}
              alt="Off-road jeep near cottage at Glow Lawns Moonrise Valley in Poilochal Chappanthottam, Wayanad"
              className="w-full h-[280px] md:h-[320px] object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
              data-testid="offroad-gallery-1"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
          
          {/* Image 2 */}
          <div className="relative overflow-hidden rounded-lg group shadow-[0_8px_30px_rgba(0,0,0,0.3)]">
            <img
              src={offRoadImages.jeep}
              alt="4x4 jeep climbing uphill terrain at Glow Lawns off-road adventure Wayanad"
              className="w-full h-[280px] md:h-[320px] object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
              data-testid="offroad-gallery-2"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
          
          {/* Image 3 */}
          <div className="relative overflow-hidden rounded-lg group shadow-[0_8px_30px_rgba(0,0,0,0.3)]">
            <img
              src={offRoadImages.view}
              alt="Mountain off-roading experience at Poilochal Chappanthottam near Glow Lawns Resort Kerala"
              className="w-full h-[280px] md:h-[320px] object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
              data-testid="offroad-gallery-3"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
        </div>
      </Section>

      {/* 3. Conversion Block */}
      <section className="py-20 bg-gradient-to-b from-[#0a0d0b] to-[#050706]">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C9A227]/10 border border-[#C9A227]/30 mb-6">
            <Mountain className="w-4 h-4 text-[#C9A227]" />
            <span className="text-xs uppercase tracking-widest text-[#C9A227]">Off-Road Adventure</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6">
            Ready to Take the <span className="text-[#C9A227]">Climb?</span>
          </h2>
          
          <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">
            Add an unforgettable off-road experience to your stay at GlowLAWNS Moonrise Valley.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <PremiumButton 
              href="/contact" 
              variant="primary" 
              className="px-8 py-4 w-full sm:w-auto"
              data-testid="offroad-reserve-btn"
            >
              <span className="inline-flex items-center gap-2">
                <Calendar size={18} />
                Reserve Your Adventure
              </span>
            </PremiumButton>
            
            <a
              href={whatsappUrl("Hi! I'm interested in the Off-Road Jeep Experience at GlowLAWNS Moonrise Valley. Please share availability and pricing.")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-green-600/20 border border-green-500/30 text-green-400 font-medium hover:bg-green-600/30 transition-all duration-300 w-full sm:w-auto"
              data-testid="offroad-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* ===== END OFF-ROADING SECTION ===== */}

      {/* Other Experiences */}
      <Section className="py-16">
        <h2 className="text-3xl md:text-4xl font-light text-white">
          Connect with nature’s rhythm
        </h2>
        <p className="mt-4 max-w-3xl text-sm md:text-base text-white/70 leading-relaxed">
          At glowLAWNS®, experiences aren’t “activities” — they’re invitations to slow down, tune in,
          and rediscover the simple joys modern life often obscures.
        </p>

        <div className="mt-12">
          <div className="text-sm font-semibold text-white">Included with your stay</div>
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
            {included.map((exp) => {
              const Icon = iconMap[exp.icon];
              return (
                <Card key={exp.id}>
                  <div className="flex items-start gap-4">
                    <div className="rounded-2xl border border-white/10 bg-black/25 p-3">
                      {Icon ? <Icon size={22} className="text-white/80" /> : null}
                    </div>
                    <div>
                      <div className="text-lg font-semibold text-white">{exp.title}</div>
                      <div className="mt-2 text-sm text-white/70">{exp.description}</div>
                      <div className="mt-4 inline-flex rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-white/70">
                        Included
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        <div className="mt-14">
          <div className="text-sm font-semibold text-white">Special add-ons</div>
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
            {addons.map((exp) => {
              const Icon = iconMap[exp.icon];
              return (
                <Card key={exp.id}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="rounded-2xl border border-white/10 bg-black/25 p-3">
                        {Icon ? <Icon size={22} className="text-white/80" /> : null}
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-white">{exp.title}</div>
                        <div className="mt-2 text-sm text-white/70">{exp.description}</div>
                      </div>
                    </div>

                    {exp.price ? (
                      <div className="shrink-0 rounded-full bg-[#C9A227]/90 px-3 py-1 text-xs font-semibold text-[#111]">
                        ₹{exp.price}
                      </div>
                    ) : null}
                  </div>

                  <div className="mt-6">
                    <PremiumButton href="/contact" variant="secondary" className="px-4 py-2">
                      <span className="inline-flex items-center gap-2">
                        <Plus size={16} /> Add to enquiry
                      </span>
                    </PremiumButton>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Experiences;
